/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: EmergencyNumberFormView.tsx
 🗓️ Created: 2025-12-04
 🧩 Description:
    Form view for creating and editing Emergency Numbers
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Phone, AlertTriangle } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { Toggle } from '../../components/ui/Toggle';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { CallerIdInput } from '../../components/ui/CallerIdInput';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import {
  fetchEmergencyNumberById,
  createEmergencyNumber,
  updateEmergencyNumber,
} from './api/emergencyNumberApi';
import type { EmergencyNumberFormState } from './types';

const defaultFormData: EmergencyNumberFormState = {
  number: '',
  description: '',
  countryCode: 'US',
  outboundRouteId: null,
  directNumber: '',
  overrideCallerId: true,
  emergencyCidNumber: '',
  emergencyCidName: '',
  forceRecording: true,
  recordingPath: '',
  notifyOnCall: true,
  notifyEmails: '',
  notifyExtensions: '',
  notifyWebhooks: '',
  testMode: false,
  testNumber: '',
  settings: {},
  enabled: true,
  notes: '',
};

export default function EmergencyNumberFormView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<EmergencyNumberFormState>(defaultFormData);
  const [originalData, setOriginalData] = useState<EmergencyNumberFormState>(defaultFormData);
  const [hasChanges, setHasChanges] = useState(false);
  const [outboundRoutes, setOutboundRoutes] = useState<{ id: number; name: string }[]>([]);

  const mode = id && id !== 'new' ? 'edit' : 'create';
  const isEditMode = mode === 'edit';

  // Load outbound routes for selector
  useEffect(() => {
    fetch(buildApiUrl('/api/telephony/outbound-routes?limit=200&sortBy=name&sortOrder=asc'), { headers: getApiHeaders() })
      .then(r => r.json())
      .then(res => {
        if (res?.success && Array.isArray(res.data)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setOutboundRoutes(res.data.map((r: any) => ({ id: r.id, name: r.name })));
        }
      })
      .catch(() => {});
  }, []);

  // Load data in edit mode
  useEffect(() => {
    if (isEditMode) {
      loadEmergencyNumber();
    }
  }, [id, isEditMode]);

  const loadEmergencyNumber = async () => {
    try {
      setLoading(true);
      const data = await fetchEmergencyNumberById(id!);
      const formState: EmergencyNumberFormState = {
        number: data.number,
        description: data.description || '',
        countryCode: data.countryCode,
        outboundRouteId: data.outboundRouteId,
        directNumber: data.directNumber || '',
        overrideCallerId: data.overrideCallerId,
        emergencyCidNumber: data.emergencyCidNumber || '',
        emergencyCidName: data.emergencyCidName || '',
        forceRecording: data.forceRecording,
        recordingPath: data.recordingPath || '',
        notifyOnCall: data.notifyOnCall,
        notifyEmails: data.notifyEmails || '',
        notifyExtensions: data.notifyExtensions || '',
        notifyWebhooks: data.notifyWebhooks || '',
        testMode: data.testMode,
        testNumber: data.testNumber || '',
        settings: data.settings || {},
        enabled: data.enabled,
        notes: data.notes || '',
      };
      setFormData(formState);
      setOriginalData(formState);
    } catch (error) {
      showError(error.message || t('emergencyNumbers:messages.loadError'));
      navigate('/pbx/emergency/numbers');
    } finally {
      setLoading(false);
    }
  };

  // Check for changes
  useEffect(() => {
    const changed = JSON.stringify(formData) !== JSON.stringify(originalData);
    setHasChanges(changed);
  }, [formData, originalData]);

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      setLoading(true);
      let savedData;
      if (isEditMode) {
        savedData = await updateEmergencyNumber(id!, formData);
        showSuccess(t('emergencyNumbers:messages.updateSuccess'));
      } else {
        savedData = await createEmergencyNumber(formData);
        showSuccess(t('emergencyNumbers:messages.createSuccess'));
      }

      // Update original data after successful save
      setOriginalData({ ...formData });
      setHasChanges(false);

      return savedData;
    } catch (error) {
      showError(error.message || t('emergencyNumbers:messages.saveError'));
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [formData, isEditMode, id, showSuccess, showError, t]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSave,
    listPath: '/pbx/emergency/numbers',
    newPath: '/pbx/emergency/numbers/new',
    mode,
    getEditPath: (savedData) => `/pbx/emergency/numbers/${savedData.id}`,
    storageKey: 'emergencyNumbers:saveAction',
  });

  // Handle cancel
  const handleCancel = useCallback(() => {
    setFormData({ ...originalData });
    setHasChanges(false);
  }, [originalData]);

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<Phone className="h-5 w-5" />}
        title={isEditMode ? `${t('emergencyNumbers:editTitle')} - ${formData.number || ''}` : t('emergencyNumbers:createTitle')}
        backButtonText={t('emergencyNumbers:actions.backToList')}
        onBack={() => navigate('/pbx/emergency/numbers')}
      />

      <div className="mt-8">
        <FormBox title={t('emergencyNumbers:title')} icon={<Phone className="h-5 w-5" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            {/* Línea 1: Emergency Number* | INPUT | Description | INPUT */}
            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.number')}
              title={t('emergencyNumbers:fields.number')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="number" required className="text-left">
                {t('emergencyNumbers:fields.number')}
              </Label>
            </SimpleTooltip>
            <Input
              id="number"
              value={formData.number}
              onChange={(e) => setFormData({ ...formData, number: e.target.value })}
              placeholder="911"
              className="font-mono"
            />

            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.description')}
              title={t('emergencyNumbers:fields.description')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="description" className="text-left">
                {t('emergencyNumbers:fields.description')}
              </Label>
            </SimpleTooltip>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('emergencyNumbers:placeholders.description')}
            />

            {/* Línea 2: Outbound Route */}
            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.outboundRouteId')}
              title={t('emergencyNumbers:fields.outboundRouteId')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="outboundRouteId" className="text-left">
                {t('emergencyNumbers:fields.outboundRouteId')}
              </Label>
            </SimpleTooltip>
            <select
              id="outboundRouteId"
              value={formData.outboundRouteId || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  outboundRouteId: e.target.value ? Number(e.target.value) : null,
                })
              }
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">{t('emergencyNumbers:placeholders.outboundRouteId')}</option>
              {outboundRoutes.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>

            {/* Línea 3: Direct Number (alternative to outbound route) */}
            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.directNumber')}
              title={t('emergencyNumbers:fields.directNumber')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="directNumber" className="text-left">
                {t('emergencyNumbers:fields.directNumber')}
              </Label>
            </SimpleTooltip>
            <Input
              id="directNumber"
              value={formData.directNumber}
              onChange={(e) => setFormData({ ...formData, directNumber: e.target.value })}
              placeholder="+1234567890"
              className="font-mono"
            />

            {/* Línea 3: Override Caller ID | TOGGLE | Emergency Caller ID | INPUT */}
            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.overrideCallerId')}
              title={t('emergencyNumbers:fields.overrideCallerId')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="overrideCallerId" className="text-left">
                {t('emergencyNumbers:fields.overrideCallerId')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="overrideCallerId"
              value={formData.overrideCallerId}
              onChange={(value) => setFormData({ ...formData, overrideCallerId: value })}
            />

            {formData.overrideCallerId ? (
              <>
                <SimpleTooltip
                  content={t('emergencyNumbers:tooltips.emergencyCid')}
                  title={t('emergencyNumbers:fields.emergencyCid')}
                  position="top"
                  maxWidth="300px"
                >
                  <Label htmlFor="emergencyCid" className="text-left">
                    {t('emergencyNumbers:fields.emergencyCid')}
                  </Label>
                </SimpleTooltip>
                <CallerIdInput
                  id="emergencyCid"
                  nameValue={formData.emergencyCidName || ''}
                  numberValue={formData.emergencyCidNumber || ''}
                  onNameChange={(value) => setFormData({ ...formData, emergencyCidName: value })}
                  onNumberChange={(value) => setFormData({ ...formData, emergencyCidNumber: value })}
                  namePlaceholder={t('emergencyNumbers:placeholders.emergencyCidName')}
                  numberPlaceholder={t('emergencyNumbers:placeholders.emergencyCidNumber')}
                  nameTooltip={t('emergencyNumbers:tooltips.emergencyCidName')}
                  numberTooltip={t('emergencyNumbers:tooltips.emergencyCidNumber')}
                />
              </>
            ) : (
              <div className="col-span-2"></div>
            )}

            {/* Línea 4: Force Call Recording | TOGGLE | Enable Notifications | TOGGLE */}
            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.forceRecording')}
              title={t('emergencyNumbers:fields.forceRecording')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="forceRecording" className="text-left">
                {t('emergencyNumbers:fields.forceRecording')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="forceRecording"
              value={formData.forceRecording}
              onChange={(value) => setFormData({ ...formData, forceRecording: value })}
            />

            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.notifyOnCall')}
              title={t('emergencyNumbers:fields.notifyOnCall')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="notifyOnCall" className="text-left">
                {t('emergencyNumbers:fields.notifyOnCall')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="notifyOnCall"
              value={formData.notifyOnCall}
              onChange={(value) => setFormData({ ...formData, notifyOnCall: value })}
            />

            {/* Línea 5: Notification Emails | INPUT | Notes | INPUT */}
            {formData.notifyOnCall ? (
              <>
                <SimpleTooltip
                  content={t('emergencyNumbers:tooltips.notifyEmails')}
                  title={t('emergencyNumbers:fields.notifyEmails')}
                  position="top"
                  maxWidth="300px"
                >
                  <Label htmlFor="notifyEmails" className="text-left">
                    {t('emergencyNumbers:fields.notifyEmails')}
                  </Label>
                </SimpleTooltip>
                <Input
                  id="notifyEmails"
                  value={formData.notifyEmails}
                  onChange={(e) => setFormData({ ...formData, notifyEmails: e.target.value })}
                  placeholder="admin@company.com, security@company.com"
                />
              </>
            ) : (
              <div className="col-span-2"></div>
            )}

            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.notes')}
              title={t('emergencyNumbers:fields.notes')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="notes" className="text-left">
                {t('emergencyNumbers:fields.notes')}
              </Label>
            </SimpleTooltip>
            <Input
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder={t('emergencyNumbers:placeholders.notes')}
            />

            {/* Línea 6: Enabled | TOGGLE */}
            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.enabled')}
              title={t('emergencyNumbers:fields.enabled')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="enabled" className="text-left">
                {t('emergencyNumbers:fields.enabled')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="enabled"
              value={formData.enabled}
              onChange={(value) => setFormData({ ...formData, enabled: value })}
            />
          </div>
        </FormBox>

        {/* Test Mode */}
        <FormBox title={t('emergencyNumbers:sections.testMode')} icon={<AlertTriangle className="h-5 w-5" />} className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            <SimpleTooltip
              content={t('emergencyNumbers:tooltips.testMode')}
              title={t('emergencyNumbers:fields.testMode')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="testMode" className="text-left">
                {t('emergencyNumbers:fields.testMode')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="testMode"
              value={formData.testMode}
              onChange={(value) => setFormData({ ...formData, testMode: value })}
            />

            {formData.testMode && (
              <>
                <SimpleTooltip
                  content={t('emergencyNumbers:tooltips.testNumber')}
                  title={t('emergencyNumbers:fields.testNumber')}
                  position="top"
                  maxWidth="300px"
                >
                  <Label htmlFor="testNumber" className="text-left">
                    {t('emergencyNumbers:fields.testNumber')}
                  </Label>
                </SimpleTooltip>
                <Input
                  id="testNumber"
                  value={formData.testNumber}
                  onChange={(e) => setFormData({ ...formData, testNumber: e.target.value })}
                  placeholder="933"
                  className="font-mono"
                />
              </>
            )}
          </div>
        </FormBox>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        isEditMode={isEditMode}
        isSaving={loading}
        cancelText={t('common:cancel')}
        useSaveDropdown={true}
        saveDropdownStorageKey="emergencyNumbers:saveAction"
        saveDisabled={loading}
      />
    </div>
  );
}
